import type { Component, Ref, UnwrapRef } from "vue";

import { v4 as uuidV4 } from "uuid";
import { defineComponent, h, inject, onBeforeMount, onBeforeUnmount, provide, ref, watch } from "vue";

interface Context<T extends object> {
  defaultValue: Partial<T>;
  keys: Ref<Array<string>>;
  Provider: Component<{ value: T }>;
}

export const useContext = <T extends object>(context: Context<T>): Ref<Required<T>> => {
  if (!context.keys.value.length) {
    return { value: context.defaultValue } as Ref<Required<T>>;
  }

  const state = inject<Ref<T>>(context.keys.value[context.keys.value.length - 1]);

  if (!state) {
    return { value: context.defaultValue } as Ref<Required<T>>;
  }

  return state as Ref<Required<T>>;
};

export const createContext = <T extends object>(defaultValue?: Partial<T>): Context<T> => {
  const CONTEXT_KEYS = ref<Array<string>>([]);

  const Provider = defineComponent<{ value: T }>(
    (props, { slots }) => {
      const KEY = uuidV4();

      onBeforeMount(() => {
        CONTEXT_KEYS.value = [...CONTEXT_KEYS.value, KEY];
      });

      onBeforeUnmount(() => {
        CONTEXT_KEYS.value = CONTEXT_KEYS.value.filter((a) => a !== KEY);
      });

      const state = ref<T>({ ...defaultValue, ...props.value });
      provide(KEY, state as any);

      watch(
        () => props.value,
        () => {
          state.value = {
            ...defaultValue,
            ...props.value,
          } as UnwrapRef<T>;
        },
        { immediate: true },
      );

      return () => {
        return h("div", {}, slots.default && slots.default());
      };
    },
    {
      props: ["value"],

      name: "Context.Provider",
    },
  );

  return {
    defaultValue: defaultValue ?? {},
    keys: CONTEXT_KEYS,
    Provider,
  };
};

export interface ITestContext {
  siteId: number;
}
